import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
    LayoutDashboard,
    User,
    Users,
    ClipboardCheck,
    GraduationCap,
    BarChart3,
    Settings,
    X,
    Menu,
} from 'lucide-react';
import { Testimonial } from '../../ui/Testimonial';

const navMain = [
    { to: '/parent/home', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/parent/children', label: 'Children', icon: Users },
    { to: '/parent/private-lessons', label: 'Private lessons', icon: GraduationCap },
    { to: '/parent/activity', label: 'Activity', icon: ClipboardCheck },
    { to: '/parent/reports', label: 'Reports', icon: BarChart3 },
];

const navAccount = [
    { to: '/parent/profile', label: 'Profile', icon: User },
    { to: '/parent/settings', label: 'Settings', icon: Settings },
];

const linkBase =
    'flex items-center gap-3 rounded-[12px] px-3.5 py-2.5 text-[14px] font-medium transition-all duration-200';
const linkIdle = 'text-white/75 hover:bg-white/[0.08] hover:text-white';
const linkActive = 'bg-white/[0.16] text-white';

function NavItem({
    to,
    label,
    icon: Icon,
    end,
    onNavigate,
}: {
    to: string;
    label: string;
    icon: React.ElementType;
    end?: boolean;
    onNavigate?: () => void;
}) {
    return (
        <NavLink
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
        >
            <Icon size={18} strokeWidth={2} className="shrink-0 opacity-95" />
            <span>{label}</span>
        </NavLink>
    );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
    return (
        <div className="flex h-full flex-col text-white">
            <div className="px-5 pt-7 pb-7">
                <Link to="/parent/home" onClick={onNavigate} className="flex items-start gap-2.5">
                    <img
                        src={`${process.env.PUBLIC_URL}/favicon.ico`}
                        alt="RYD Learning"
                        className="mt-0.5 h-9 w-9 shrink-0 object-contain"
                    />
                    <div className="min-w-0">
                        <p className="font-[AvertaStd-Semibold] text-[16px] leading-tight text-white">
                            RYD Learning
                        </p>
                        <p className="mt-0.5 text-[12px] font-normal leading-snug text-white/65">
                            Activity Board
                        </p>
                    </div>
                </Link>
            </div>

            <nav className="flex-1 overflow-y-auto px-3">
                <div className="space-y-0.5">
                    {navMain.map((item) => (
                        <NavItem key={item.to} {...item} onNavigate={onNavigate} />
                    ))}
                </div>

                <div className="my-5 mx-2 border-t border-white/15" />

                <p className="mb-2 px-3.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">
                    Account
                </p>
                <div className="space-y-0.5">
                    {navAccount.map((item) => (
                        <NavItem key={item.to} {...item} onNavigate={onNavigate} />
                    ))}
                </div>
            </nav>

            <div className="mt-auto p-4 pt-2">
                <Testimonial />
            </div>
        </div>
    );
}

function SidebarShell({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex h-full flex-col bg-ryd-primary">
            {children}
        </div>
    );
}

export default function Sidebar() {
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                aria-label="Open menu"
                onClick={() => setMobileOpen(true)}
                className="fixed left-3 top-3 z-40 flex h-10 w-10 items-center justify-center rounded-xl bg-ryd-primary text-white shadow-lg sm:left-4 sm:top-4 sm:h-11 sm:w-11 lg:hidden"
            >
                <Menu size={20} />
            </button>

            <aside className="fixed left-0 top-0 z-30 hidden h-screen w-[260px] lg:block">
                <SidebarShell>
                    <SidebarContent />
                </SidebarShell>
            </aside>

            {mobileOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <button
                        type="button"
                        aria-label="Close menu"
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => setMobileOpen(false)}
                    />
                    <aside className="absolute left-0 top-0 h-full w-[min(280px,88vw)] shadow-2xl">
                        <SidebarShell>
                            <button
                                type="button"
                                aria-label="Close menu"
                                onClick={() => setMobileOpen(false)}
                                className="absolute right-3 top-4 z-10 rounded-lg p-2 text-white/80 hover:bg-white/10"
                            >
                                <X size={18} />
                            </button>
                            <SidebarContent onNavigate={() => setMobileOpen(false)} />
                        </SidebarShell>
                    </aside>
                </div>
            )}
        </>
    );
}
